import React, { useState, useEffect } from "react";
import {
  Shield, FileText, CheckCircle2, Building2, Users,
  ExternalLink, Lock, X, Send, ChevronRight,
  AlertCircle, CheckCircle, Globe, Download, Copy,
  Link2, Mail, Search, ChevronDown, Menu, Award,
} from "lucide-react";
import ReclaimAccessModal from "./ReclaimAccessModal";

const TC_API = "https://api.calvant.com/trust-service"

// ── Session token helpers ─────────────────────────────────────────────────────

const SESSION_KEY = "tc_session_token";

const saveSession = (org, token) =>
  sessionStorage.setItem(`${SESSION_KEY}_${org}`, token);

const loadSession = (org) =>
  sessionStorage.getItem(`${SESSION_KEY}_${org}`) || null;

const clearSession = (org) =>
  sessionStorage.removeItem(`${SESSION_KEY}_${org}`);

// ── Compliance colours ────────────────────────────────────────────────────────

const COMPLIANCE_COLORS = {
  SOC2:     { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  ISO27001: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0" },
  ISO:      { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0" },
  HIPAA:    { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  GDPR:     { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  PCI:      { bg: "#fefce8", text: "#a16207", border: "#fef08a" },
  DEFAULT:  { bg: "#f8fafc", text: "#475569", border: "#e2e8f0" },
};
const getComplianceColor = (label = "") => {
  const upper = label.toUpperCase();
  const key   = Object.keys(COMPLIANCE_COLORS).find((k) => upper.includes(k));
  return COMPLIANCE_COLORS[key] || COMPLIANCE_COLORS.DEFAULT;
};

// ── Request Access Modal ──────────────────────────────────────────────────────

const REASONS = [
  "Security due diligence", "Vendor assessment", "Compliance review",
  "Customer evaluation", "Partnership", "Other",
];

const INITIAL_FORM = {
  firstName: "", lastName: "", requesterEmail: "",
  requesterCompany: "", reason: "", message: "",
};

const Field = ({ label, placeholder, value, onChange, error, type = "text", children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{label}</label>
    {children || (
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{
          borderRadius: 6, border: error ? "1.5px solid #ef4444" : "1.5px solid #d1d5db",
          padding: "9px 12px", fontSize: 13, color: "#111827",
          outline: "none", boxSizing: "border-box", width: "100%",
        }}
        onFocus={(e) => { e.target.style.borderColor = error ? "#ef4444" : "#1d4ed8"; }}
        onBlur={(e)  => { e.target.style.borderColor = error ? "#ef4444" : "#d1d5db"; }}
      />
    )}
    {error && <span style={{ fontSize: 11, color: "#ef4444" }}>{error}</span>}
  </div>
);

const RequestAccessModal = ({ targetOrg, shareToken, organization, policyName, onClose, policies = [] }) => {
  const [form, setForm]                       = useState(INITIAL_FORM);
  const [selectedResources, setSelectedResources] = useState(
    policyName && policyName !== "full" ? [policyName] : []
  );
  const [errors, setErrors]   = useState({});
  const [status, setStatus]   = useState("idle");
  const [errMsg, setErrMsg]   = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const validate = () => {
    const e = {};
    if (!form.firstName.trim())      e.firstName      = "Required";
    if (!form.lastName.trim())       e.lastName       = "Required";
    if (!form.requesterEmail.trim()) e.requesterEmail = "Required";
    else if (!/\S+@\S+\.\S+/.test(form.requesterEmail)) e.requesterEmail = "Valid email required";
    if (!form.requesterCompany.trim()) e.requesterCompany = "Required";
    return e;
  };

  const hc = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const toggleResource = (name) =>
    setSelectedResources((prev) =>
      prev.includes(name) ? prev.filter((r) => r !== name) : [...prev, name]
    );

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus("submitting");
    setErrMsg("");

    const payload = {
      targetOrganization: targetOrg || organization,
      requesterName:    `${form.firstName.trim()} ${form.lastName.trim()}`,
      requesterEmail:   form.requesterEmail.trim(),
      requesterCompany: form.requesterCompany.trim(),
      message: [form.reason, form.message].filter(Boolean).join(" — ") || null,
      requesterType: "CUSTOMER_COMPANY",
      targetPolicyName: policyName === "full" ? null : (policyName || null),
    };

    try {
      let url;
      if (shareToken) {
        url = `${TC_API}/api/access-requests/public?shareToken=${encodeURIComponent(shareToken)}`;
      } else if (organization) {
        url = `${TC_API}/api/access-requests/public/by-org?organization=${encodeURIComponent(organization)}`;
      } else {
        setErrMsg("Unable to submit — missing trust centre token.");
        setStatus("error");
        return;
      }
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 409) { setErrMsg((await res.text()) || "Request already exists."); setStatus("error"); return; }
      if (!res.ok) throw new Error(await res.text());
      setStatus("success");
    } catch (err) {
      setErrMsg(err.message || "Something went wrong.");
      setStatus("error");
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "white", borderRadius: 12, width: "100%", maxWidth: 520,
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
        maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px", borderBottom: "1px solid #e5e7eb",
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>Request access</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 4, borderRadius: 6, display: "flex" }}>
            <X size={18} />
          </button>
        </div>

        {status === "success" ? (
          <div style={{ padding: "48px 24px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircle size={28} color="#22c55e" />
            </div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>Request submitted!</h3>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6, maxWidth: 320 }}>
              Your request has been received. An admin will review it and get back to you at{" "}
              <strong>{form.requesterEmail}</strong>.
            </p>
            <button onClick={onClose} style={{ marginTop: 8, padding: "10px 32px", borderRadius: 8, border: "none", background: "#1d4ed8", color: "white", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Done</button>
          </div>
        ) : (
          <div style={{ overflowY: "auto", flex: 1 }}>
            <div style={{ padding: "12px 24px 0", fontSize: 13, color: "#6b7280" }}>
              Already have access?{" "}
              {/* This button is intentionally left as a passthrough — parent handles it */}
              <button onClick={onClose} data-reclaim="true" style={{ background: "none", border: "none", color: "#1d4ed8", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0 }}>
                Reclaim access
              </button>
            </div>

            <div style={{ padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              {status === "error" && (
                <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca" }}>
                  <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: "#b91c1c" }}>{errMsg}</span>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="First name" placeholder="Jane" value={form.firstName} onChange={hc("firstName")} error={errors.firstName} />
                <Field label="Last name"  placeholder="Smith" value={form.lastName}  onChange={hc("lastName")}  error={errors.lastName}  />
              </div>

              <Field label="Email"        placeholder="jane@company.com" type="email" value={form.requesterEmail}   onChange={hc("requesterEmail")}   error={errors.requesterEmail}   />
              <Field label="Company name" placeholder="Acme Corp"                     value={form.requesterCompany} onChange={hc("requesterCompany")} error={errors.requesterCompany} />

              <Field label="Reason">
                <div style={{ position: "relative" }}>
                  <select value={form.reason} onChange={hc("reason")} style={{ width: "100%", appearance: "none", borderRadius: 6, border: "1.5px solid #d1d5db", padding: "9px 36px 9px 12px", fontSize: 13, color: form.reason ? "#111827" : "#9ca3af", background: "white", cursor: "pointer", outline: "none" }}>
                    <option value="">Select a reason</option>
                    {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <ChevronDown size={14} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#6b7280", pointerEvents: "none" }} />
                </div>
              </Field>

              {(policyName === "full" || !policyName) && policies.length > 0 && (
                <Field label="Resources">
                  <div style={{ border: "1.5px solid #d1d5db", borderRadius: 6, overflow: "hidden", maxHeight: 180, overflowY: "auto" }}>
                    {policies.map((p, i) => {
                      const name    = (p.name && !p.name.includes("/")) ? p.name : `Document ${i + 1}`;
                      const checked = selectedResources.includes(name);
                      return (
                        <label key={name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", cursor: "pointer", borderBottom: i < policies.length - 1 ? "1px solid #f3f4f6" : "none", background: checked ? "#eff6ff" : "white" }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleResource(name)} style={{ accentColor: "#1d4ed8", width: 15, height: 15 }} />
                          <FileText size={13} color="#6b7280" />
                          <span style={{ fontSize: 13, color: "#374151", fontWeight: checked ? 600 : 400 }}>{name}</span>
                        </label>
                      );
                    })}
                  </div>
                </Field>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  Message <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span>
                </label>
                <textarea rows={3} value={form.message} onChange={hc("message")} placeholder="Anything else you'd like us to know" style={{ borderRadius: 6, border: "1.5px solid #d1d5db", padding: "9px 12px", fontSize: 13, color: "#111827", resize: "vertical", fontFamily: "inherit", outline: "none", boxSizing: "border-box", width: "100%" }}
                  onFocus={(e) => { e.target.style.borderColor = "#1d4ed8"; }}
                  onBlur={(e)  => { e.target.style.borderColor = "#d1d5db"; }}
                />
              </div>
            </div>
          </div>
        )}

        {status !== "success" && (
          <div style={{ padding: "14px 24px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{ padding: "9px 20px", borderRadius: 8, border: "1px solid #d1d5db", background: "white", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
            <button onClick={handleSubmit} disabled={status === "submitting"} style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: status === "submitting" ? "#93c5fd" : "#1d4ed8", color: "white", fontWeight: 600, fontSize: 13, cursor: status === "submitting" ? "default" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
              {status === "submitting" ? (
                <><span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.4)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite", display: "inline-block" }} />Submitting…</>
              ) : "Request access"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = ["Overview", "Resources", "Certifications", "Controls", "Subprocessors"];

export default function PublicTrustCentrePage() {
  const [tc, setTc]                           = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [shareToken, setShareToken]           = useState(null);
  const [activeTab, setActiveTab]             = useState(0);
  const [searchQuery, setSearchQuery]         = useState("");
  const [activePolicyCategory, setActivePolicyCategory] = useState(null);
  const [modalTarget, setModalTarget]         = useState(null);   // null | "full" | policyName

  // ── Session / Reclaim ────────────────────────────────────────────────────
  const [sessionToken, setSessionToken]       = useState(null);   // set after reclaim OTP
  const [showReclaim, setShowReclaim]         = useState(false);

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const pathname = window.location.pathname;
    const segments = pathname.split("/").filter(Boolean);
    const idx      = segments.findIndex((s) => s === "share" || s === "trust-centre");
    const tokenFromPath  = idx !== -1 ? segments[idx + 1] : null;
    const tokenFromQuery = new URLSearchParams(window.location.search).get("shareToken");
    const token = tokenFromPath || tokenFromQuery || null;
    setShareToken(token);

    const hostname = window.location.hostname;
    //const hostname = "trust.consultantsfactory.com";
    const isCustomDomain =
      !hostname.includes("calvant.com") &&
      !hostname.includes("localhost") &&
      !hostname.includes("127.0.0.1");

    let fetchUrl = null;
    if (isCustomDomain) {
      fetchUrl = `${TC_API}/api/trust-centre/public/by-domain?domain=${encodeURIComponent(hostname)}`;
    } else if (token) {
      fetchUrl = `${TC_API}/api/preview/share/${encodeURIComponent(token)}`;
    }

    if (!fetchUrl) { setError("No share token or custom domain found."); setLoading(false); return; }

    fetch(fetchUrl)
      .then((r) => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((data) => {
        setTc(data);
        if (data?.companyName) document.title = `${data.companyName} - Trust Center`;
        // Restore any saved session for this org
        if (data?.organization) {
          const saved = loadSession(data.organization);
          if (saved) setSessionToken(saved);
        }
      })
      .catch(() => setError("Trust Centre not found."))
      .finally(() => setLoading(false));
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────────
  const effectiveToken  = shareToken || tc?.shareToken || null;
  const tcOrganization  = tc?.organization || null;
  const logoUrl         = tc ? `${TC_API}/api/preview/${encodeURIComponent(tc.organization)}/logo` : null;
  const hasSession      = Boolean(sessionToken);

  const policies        = tc?.policies        || [];
  const certifications  = tc?.certifications  || [];

  const policyGroups = policies.reduce((acc, p) => {
    const displayName = (p.name && !p.name.includes("/")) ? p.name : "Document";
    const cat = displayName.split(/[\s\-_]/)[0] || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ ...p, displayName });
    return acc;
  }, {});
  const categories = Object.keys(policyGroups);

  useEffect(() => {
    if (categories.length > 0 && !activePolicyCategory) {
      setActivePolicyCategory(categories[0]);
    }
  }, [categories.length]);

  const filteredPolicies = searchQuery
    ? policies.filter((p) => {
        const dn = (p.name && !p.name.includes("/")) ? p.name : "Document";
        return dn.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : (activePolicyCategory ? (policyGroups[activePolicyCategory] || []) : policies)
        .map((p) => ({ ...p, displayName: (p.name && !p.name.includes("/")) ? p.name : "Document" }));

  // ── Authenticated download helper ────────────────────────────────────────
  // Builds auth headers using sessionToken (reclaimed access) or shareToken (link access)
  const buildAuthHeaders = () => {
    if (sessionToken) return { "X-TC-Session": sessionToken };
    return {};
  };

  const handleDownloadPolicy = async (policyName) => {
    if (!hasSession) { setModalTarget(policyName); return; }
    try {
      const params = new URLSearchParams({ organization: tc.organization });
      if (effectiveToken) params.append("shareToken", effectiveToken);
      const res = await fetch(
        `${TC_API}/api/trust-centre/policies/${encodeURIComponent(policyName)}/download?${params}`,
        { headers: buildAuthHeaders() }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = policyName; a.click();
    } catch {
      alert("Download failed. Your session may have expired — please reclaim access.");
    }
  };

  const handleViewCert = async (certName) => {
    if (!hasSession) { setShowReclaim(true); return; }
    try {
      const params = new URLSearchParams({ organization: tc.organization });
      if (effectiveToken) params.append("shareToken", effectiveToken);
      const res = await fetch(
        `${TC_API}/api/trust-centre/certifications/${encodeURIComponent(certName)}/download?${params}`,
        { headers: buildAuthHeaders() }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      alert("Could not open certificate. Please reclaim access and try again.");
    }
  };

  const handleReclaimSuccess = (token) => {
    setSessionToken(token);
    saveSession(tc.organization, token);
    setShowReclaim(false);
  };

  const handleSignOut = () => {
    clearSession(tc.organization);
    setSessionToken(null);
  };

  // ── Loading / Error ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 14, background: "#f9fafb", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <div style={{ width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: "3px solid #1d4ed8", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      <p style={{ color: "#6b7280", margin: 0, fontSize: 14 }}>Loading Trust Center…</p>
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );

  if (error || !tc) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, background: "#f9fafb", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <Shield size={40} color="#d1d5db" />
      <h2 style={{ color: "#111827", margin: 0 }}>Trust Center not found</h2>
      <p style={{ color: "#6b7280", margin: 0, fontSize: 14 }}>{error}</p>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: "#111827" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        a { text-decoration: none; }
      `}</style>

      {/* ── Top Nav ───────────────────────────────────────────────────────── */}
      <nav style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 60, position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, overflow: "hidden", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src={logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }}
              onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-weight:800;font-size:15px;color:#1d4ed8;">${(tc.companyName || "?")[0]}</span>`; }} />
          </div>
          <div style={{ width: "1px", height: 20, background: "#e5e7eb" }} />
          <span style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
            {tc.companyName}'s Trust Center
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Session indicator */}
          {hasSession && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 20, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
              <CheckCircle size={13} color="#16a34a" />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>Access granted</span>
              <button onClick={handleSignOut} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 11, padding: "0 0 0 4px", fontWeight: 500 }}>
                Sign out
              </button>
            </div>
          )}

          {/* Reclaim access button (only when no active session) */}
          {!hasSession && (
            <button onClick={() => setShowReclaim(true)} style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "white", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <CheckCircle size={13} color="#6b7280" />
              Reclaim access
            </button>
          )}

          <button onClick={() => setModalTarget("full")} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#1d4ed8", color: "white", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Lock size={13} />
            Request access
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%)", padding: "56px 32px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: "8%", top: "50%", transform: "translateY(-50%)", width: 260, height: 260, opacity: 0.12, background: "radial-gradient(circle, white 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", right: "6%", top: "50%", transform: "translateY(-50%)", opacity: 0.9, pointerEvents: "none" }}>
          <svg width="220" height="200" viewBox="0 0 220 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="80" y="20" width="110" height="140" rx="8" fill="white" fillOpacity="0.95"/>
            <rect x="95" y="40" width="80" height="6" rx="3" fill="#bfdbfe"/>
            <rect x="95" y="54" width="60" height="4" rx="2" fill="#dbeafe"/>
            <rect x="95" y="65" width="70" height="4" rx="2" fill="#dbeafe"/>
            <rect x="95" y="80" width="75" height="30" rx="3" fill="#eff6ff"/>
            <rect x="100" y="98" width="8" height="8" rx="1" fill="#3b82f6"/>
            <rect x="113" y="93" width="8" height="13" rx="1" fill="#1d4ed8"/>
            <rect x="126" y="88" width="8" height="18" rx="1" fill="#60a5fa"/>
            <rect x="139" y="84" width="8" height="22" rx="1" fill="#2563eb"/>
            <path d="M18 52 L55 38 L92 52 L92 85 C92 105 55 120 55 120 C55 120 18 105 18 85 Z" fill="#1e40af" stroke="#60a5fa" strokeWidth="2"/>
            <path d="M28 62 L55 52 L82 62 L82 87 C82 102 55 113 55 113 C55 113 28 102 28 87 Z" fill="#1d4ed8"/>
            <path d="M40 83 L50 93 L72 71" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div style={{ maxWidth: 600, position: "relative", zIndex: 1 }}>
          <h1 style={{ margin: "0 0 10px", color: "white", fontWeight: 700, fontSize: 28 }}>{tc.companyName}</h1>
          {tc.overview && (
            <p style={{ margin: "0 0 18px", color: "rgba(255,255,255,0.8)", fontSize: 15, lineHeight: 1.6, maxWidth: 500 }}>{tc.overview}</p>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20, marginTop: 8 }}>
            {tc.domain && (
              <a href={`https://${tc.domain}`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
                <Mail size={14} />security@{tc.domain}
              </a>
            )}
            {tc.privacyPolicyLink && (
              <a href={tc.privacyPolicyLink} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
                <Link2 size={14} />Privacy Policy
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab Bar ──────────────────────────────────────────────────────── */}
      <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "0 32px" }}>
        <div style={{ display: "flex", gap: 0 }}>
          {TABS.map((tab, i) => (
            <button key={tab} onClick={() => setActiveTab(i)} style={{
              padding: "14px 18px", border: "none", background: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 500,
              color: activeTab === i ? "#1d4ed8" : "#6b7280",
              borderBottom: activeTab === i ? "2px solid #1d4ed8" : "2px solid transparent",
            }}>
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab Content ──────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px" }}>

        {/* ══ OVERVIEW ══ */}
        {activeTab === 0 && (
          <div style={{ display: "grid", gap: 24 }}>
            {tc.compliances?.length > 0 && (
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Compliance</h2>
                  <button onClick={() => setActiveTab(3)} style={{ background: "none", border: "none", color: "#1d4ed8", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>View all</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                  {tc.compliances.map((c) => {
                    const col = getComplianceColor(c);
                    return (
                      <div key={c} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 10, background: col.bg, border: `1px solid ${col.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CheckCircle2 size={20} color={col.text} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{c}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Certified</div>
                        </div>
                        <ChevronRight size={16} color="#d1d5db" style={{ marginLeft: "auto" }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tc.trustedBy?.length > 0 && (
              <div>
                <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 700 }}>Trusted by</h2>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {tc.trustedBy.map((entry) => (
                    <div key={entry.companyName} style={{ padding: "8px 16px", borderRadius: 8, background: "white", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 8 }}>
                      <img src={`${TC_API}/api/trust-centre/trusted-by/${encodeURIComponent(tc.organization)}/${encodeURIComponent(entry.companyName)}/icon`} alt="" width={16} height={16} style={{ objectFit: "contain", borderRadius: 3 }} onError={(e) => { e.target.style.display = "none"; }} />
                      <Building2 size={13} color="#94a3b8" />
                      {entry.companyName}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ RESOURCES (Policies) ══ */}
        {activeTab === 1 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Resources</h2>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ position: "relative" }}>
                  <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                  <input type="text" placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ padding: "8px 12px 8px 32px", borderRadius: 8, fontSize: 13, border: "1px solid #d1d5db", outline: "none", color: "#374151", background: "white", width: 200 }} />
                </div>
              </div>
            </div>

            {policies.length === 0 ? (
              <div style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: 10, padding: "48px 24px", textAlign: "center", color: "#9ca3af" }}>
                <FileText size={36} style={{ marginBottom: 10 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No policy documents available.</p>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
                {!searchQuery && categories.length > 1 && (
                  <div style={{ width: 160, flexShrink: 0 }}>
                    {categories.map((cat) => (
                      <button key={cat} onClick={() => setActivePolicyCategory(cat)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", cursor: "pointer", fontSize: 13, borderRadius: 6, color: activePolicyCategory === cat ? "#1d4ed8" : "#6b7280", fontWeight: activePolicyCategory === cat ? 700 : 400, background: activePolicyCategory === cat ? "#eff6ff" : "none" }}>
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 0 }}>
                  {!searchQuery && activePolicyCategory && (
                    <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#111827" }}>{activePolicyCategory}</h3>
                  )}
                  <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                    {filteredPolicies.map((p, i) => {
                      const displayName = p.displayName || ((p.name && !p.name.includes("/")) ? p.name : `Document ${i + 1}`);
                      const fileExt = p.contentType ? (p.contentType.includes("pdf") ? "PDF" : p.contentType.includes("word") ? "DOCX" : p.contentType.split("/")[1]?.toUpperCase() || "DOC") : "DOC";
                      return (
                        <div key={p.name || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: i < filteredPolicies.length - 1 ? "1px solid #f3f4f6" : "none", gap: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                            <FileText size={16} color="#6b7280" style={{ flexShrink: 0 }} />
                            <span style={{ fontWeight: 500, fontSize: 14, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "#f1f5f9", color: "#64748b", flexShrink: 0 }}>{fileExt}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            {hasSession ? (
                              /* UNLOCKED: show download button */
                              <button onClick={() => handleDownloadPolicy(displayName)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 6, border: "1px solid #bbf7d0", background: "#f0fdf4", fontSize: 12, fontWeight: 600, color: "#15803d", cursor: "pointer" }}>
                                <Download size={12} />
                                Download
                              </button>
                            ) : (
                              /* LOCKED: request access */
                              <button onClick={() => setModalTarget(displayName)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 6, border: "1px solid #d1d5db", background: "white", fontSize: 12, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                                <Lock size={12} />
                                Request access
                              </button>
                            )}
                            <button onClick={() => navigator.clipboard?.writeText(window.location.href)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: "white", fontSize: 12, fontWeight: 500, color: "#6b7280", cursor: "pointer" }}>
                              <Copy size={12} />
                              Copy link
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ CERTIFICATIONS ══ */}
        {activeTab === 2 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Certifications</h2>
              {!hasSession && (
                <button onClick={() => setShowReclaim(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #d1d5db", background: "white", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>
                  <CheckCircle size={14} color="#6b7280" />
                  Reclaim access to view
                </button>
              )}
            </div>

            {certifications.length === 0 ? (
              <div style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: 10, padding: "48px 24px", textAlign: "center", color: "#9ca3af" }}>
                <Award size={36} style={{ marginBottom: 10 }} />
                <p style={{ margin: 0, fontSize: 14 }}>No certifications available.</p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
                {certifications.map((cert) => {
                  const col = getComplianceColor(cert.standard || cert.name || "");
                  return (
                    <div key={cert.name} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
                      {/* Card header */}
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: col.bg, border: `1px solid ${col.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Award size={20} color={col.text} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", lineHeight: 1.3 }}>{cert.name}</div>
                          {cert.standard && (
                            <span style={{ display: "inline-block", marginTop: 4, padding: "2px 8px", borderRadius: 4, background: col.bg, color: col.text, fontSize: 11, fontWeight: 700, border: `1px solid ${col.border}` }}>
                              {cert.standard}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Meta */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {cert.issuedBy && (
                          <div style={{ display: "flex", gap: 8, fontSize: 13, color: "#6b7280" }}>
                            <span style={{ fontWeight: 600, color: "#374151", minWidth: 72 }}>Issued by</span>
                            <span>{cert.issuedBy}</span>
                          </div>
                        )}
                        {cert.validUntil && (
                          <div style={{ display: "flex", gap: 8, fontSize: 13, color: "#6b7280" }}>
                            <span style={{ fontWeight: 600, color: "#374151", minWidth: 72 }}>Valid until</span>
                            <span>{new Date(cert.validUntil).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                          </div>
                        )}
                      </div>

                      {/* Action */}
                      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 14 }}>
                        {hasSession ? (
                          <button onClick={() => handleViewCert(cert.name)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 0", borderRadius: 8, border: "1px solid #bbf7d0", background: "#f0fdf4", fontSize: 13, fontWeight: 600, color: "#15803d", cursor: "pointer" }}>
                            <Download size={14} />
                            View certificate
                          </button>
                        ) : (
                          <button onClick={() => setShowReclaim(true)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 0", borderRadius: 8, border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: 13, fontWeight: 600, color: "#6b7280", cursor: "pointer" }}>
                            <Lock size={14} />
                            Requires access
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ CONTROLS ══ */}
        {activeTab === 3 && (
          <div>
            <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 700 }}>Controls</h2>
            {tc.compliances?.length > 0 ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                {tc.compliances.map((c) => {
                  const col = getComplianceColor(c);
                  return (
                    <div key={c} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: "20px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                        <div style={{ width: 38, height: 38, borderRadius: 8, background: col.bg, border: `1px solid ${col.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Shield size={18} color={col.text} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{c}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>Compliance Framework</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {["Access Controls", "Data Encryption", "Incident Response", "Risk Management"].map((ctrl) => (
                          <div key={ctrl} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <CheckCircle size={14} color="#22c55e" />
                            <span style={{ fontSize: 13, color: "#374151" }}>{ctrl}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: 10, padding: "48px 24px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>No controls information available.</div>
            )}
          </div>
        )}

        {/* ══ SUBPROCESSORS ══ */}
        {activeTab === 4 && (
          <div>
            <h2 style={{ margin: "0 0 16px", fontSize: 18, fontWeight: 700 }}>Subprocessors</h2>
            {tc.subProcessors?.length > 0 ? (
              <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                      {["Name", "Purpose", "Location", "Website"].map((h) => (
                        <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tc.subProcessors.map((sp, i) => (
                      <tr key={sp.name} style={{ borderBottom: i < tc.subProcessors.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                        <td style={{ padding: "14px 16px", fontSize: 14, fontWeight: 600 }}>{sp.name}</td>
                        <td style={{ padding: "14px 16px", fontSize: 13, color: "#6b7280" }}>{sp.purpose}</td>
                        <td style={{ padding: "14px 16px", fontSize: 13, color: "#6b7280" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Globe size={13} />{sp.location}</div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          {sp.website && <a href={sp.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#1d4ed8", display: "flex", alignItems: "center", gap: 4 }}><ExternalLink size={12} />{sp.website.replace(/^https?:\/\//, "")}</a>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ background: "white", border: "1px dashed #d1d5db", borderRadius: 10, padding: "48px 24px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>No subprocessors listed.</div>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer style={{ background: "white", borderTop: "1px solid #e5e7eb", padding: "16px 32px", textAlign: "center", marginTop: 24 }}>
        <p style={{ margin: 0, color: "#9ca3af", fontSize: 12 }}>
          {`© ${new Date().getFullYear()} ${tc.companyName || ""} · Powered by `}
          <a href="https://calvant.com" target="_blank" rel="noreferrer" style={{ color: "#1d4ed8", fontWeight: 600 }}>CalVant</a>
        </p>
      </footer>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {/* Request Access modal */}
      {modalTarget && (
        <RequestAccessModal
          targetOrg={tc.organization}
          shareToken={effectiveToken}
          organization={tcOrganization}
          policyName={modalTarget}
          policies={policies}
          onClose={() => setModalTarget(null)}
        />
      )}

      {/* Reclaim Access modal */}
      {showReclaim && (
        <ReclaimAccessModal
          organization={tcOrganization}
          shareToken={effectiveToken}
          onClose={() => setShowReclaim(false)}
          onSuccess={handleReclaimSuccess}
        />
      )}
    </div>
  );
}